import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import {
  AggregatedDto,
  AnalyticSearchRequestDto,
  ChartReportDto,
  ChartReportRequestDto,
  ChartResponseDto,
  FiltersDataDto,
  FiltersRequestDto,
  KeyMetricsResponseDto,
} from 'src/generated/services/TFinancialApi';
import { addDays, addMonths, addQuarters, addWeeks, addYears, format, parse } from 'date-fns';
import { formatDate } from 'src/utils/date';
import { AxiosResponse } from 'axios';
import { openToastError } from 'src/use-cases/toast/useToast';
import { useState } from 'react';
import { sanitizeFormValues } from './utils/sanitizeFormValues';
import { TableTab } from './table/useTable';
import { mergeTableFiltersIntoRequest } from './table/analyticsSearchRequestBuilders';
import useToast from 'src/use-cases/toast/useToast';
import DocumentDownloadIcon from 'src/assets/images/icons/document-download.svg';

/**
 * Converts null values to the string "null" for API compatibility.
 * Empty strings are preserved as-is.
 * Backend requires:
 * - null → "null" (string literal)
 * - "" → "" (empty string preserved)
 *
 * @param obj - Object to convert
 */
export const convertNullToStringLiteral = (obj: any) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === null) {
      obj[key] = 'null'; // Convert null to string "null"
    } else if (obj[key] === '') {
      // Keep empty string as-is
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      convertNullToStringLiteral(obj[key]);
    }
  });
  return obj;
};

/**
 * Process invoiceStatusSet to handle different input types
 * @param statusSet - The invoice status set which could be an array, string, or undefined
 * @returns Properly formatted array of invoice statuses with correct types
 */
export const processInvoiceStatusSet = (statusSet: any): ('FLAGGED' | 'APPROVED' | 'TO_APPROVE' | 'PENDING_DOCUMENT' | 'REJECTED' | 'TO_REJECT')[] => {
  const validStatuses = ['FLAGGED', 'APPROVED', 'TO_APPROVE', 'PENDING_DOCUMENT', 'REJECTED', 'TO_REJECT'];

  if (Array.isArray(statusSet)) {
    return statusSet.filter((status) => validStatuses.includes(status)) as (
      | 'FLAGGED'
      | 'APPROVED'
      | 'TO_APPROVE'
      | 'PENDING_DOCUMENT'
      | 'REJECTED'
      | 'TO_REJECT'
    )[];
  }

  if (typeof statusSet === 'string' && statusSet.length > 0) {
    const statusArray = statusSet.split(',');
    return statusArray.filter((status) => validStatuses.includes(status)) as (
      | 'FLAGGED'
      | 'APPROVED'
      | 'TO_APPROVE'
      | 'PENDING_DOCUMENT'
      | 'REJECTED'
      | 'TO_REJECT'
    )[];
  }

  return [];
};

export const useChart = (filter: AnalyticSearchRequestDto, options?: { enabled?: boolean }): UseQueryResult<ChartResponseDto> => {
  const newFilter: FiltersRequestDto = {
    ...filter,
    invoiceStatusSet: processInvoiceStatusSet(filter.invoiceStatusSet),
  };

  return useQuery<ChartResponseDto>(
    cacheKeysUtil.getChart(filter),
    async (): Promise<ChartResponseDto> => {
      const response = await tFinancialService.api.searchChartData(filter);
      const { data } = response.data;
      const completeDates = fillMissingDates(data, filter.granularity);
      return completeDates;
    },
    {
      enabled: !!filter.currencyReference && options?.enabled === true,
    }
  );
};

export const useKeyMetrics = (
  useType: 'DASHBOARD' | 'REPORT' | 'PERFORMANCE_ASSESSMENT',
  filter: AnalyticSearchRequestDto,
  options?: { enabled?: boolean }
): UseQueryResult<KeyMetricsResponseDto> => {
  const newFilter: AnalyticSearchRequestDto = {
    ...filter,
    invoiceStatusSet: processInvoiceStatusSet(filter.invoiceStatusSet),
  };

  return useQuery(
    cacheKeysUtil.getKeyMetrics(filter),
    async (): Promise<KeyMetricsResponseDto> => (await tFinancialService.api.getKeyMetricsWithFilter(useType, filter)).data.data,
    {
      enabled: !!filter.currencyReference && options?.enabled !== false,
    }
  );
};

/**
 * Hook for fetching invoice-only key metrics data (invoice count, total amounts before/after tax)
 * Loads independently to improve performance - can be used in parallel with other metrics
 */
export const useKeyMetricsInvoiceData = (
  useType: 'DASHBOARD' | 'REPORT' | 'PERFORMANCE_ASSESSMENT',
  filter: AnalyticSearchRequestDto,
  options?: { enabled?: boolean }
): UseQueryResult<KeyMetricsResponseDto> => {
  const newFilter: AnalyticSearchRequestDto = {
    ...filter,
    invoiceStatusSet: processInvoiceStatusSet(filter.invoiceStatusSet),
  };

  return useQuery(
    [...cacheKeysUtil.getKeyMetrics(filter), 'invoice-data'],
    async (): Promise<KeyMetricsResponseDto> => (await tFinancialService.api.getKeyMetricsInvoiceData(useType, newFilter)).data.data,
    {
      enabled: !!filter.currencyReference && options?.enabled !== false,
    }
  );
};

/**
 * Hook for fetching shipment-only key metrics data (shipment count, volume, weights, CO2 emissions)
 * Loads independently to improve performance - can be used in parallel with other metrics
 */
export const useKeyMetricsShipmentData = (
  useType: 'DASHBOARD' | 'REPORT' | 'PERFORMANCE_ASSESSMENT',
  filter: AnalyticSearchRequestDto,
  options?: { enabled?: boolean }
): UseQueryResult<KeyMetricsResponseDto> => {
  const newFilter: AnalyticSearchRequestDto = {
    ...filter,
    invoiceStatusSet: processInvoiceStatusSet(filter.invoiceStatusSet),
  };

  return useQuery(
    [...cacheKeysUtil.getKeyMetrics(filter), 'shipment-data'],
    async (): Promise<KeyMetricsResponseDto> => (await tFinancialService.api.getKeyMetricsShipmentData(useType, newFilter)).data.data,
    {
      enabled: !!filter.currencyReference && options?.enabled !== false,
    }
  );
};

/**
 * Hook for fetching charge-only key metrics data (charge count, deviation amounts before/after tax)
 * Loads independently to improve performance - can be used in parallel with other metrics
 */
export const useKeyMetricsChargeData = (
  useType: 'DASHBOARD' | 'REPORT' | 'PERFORMANCE_ASSESSMENT',
  filter: AnalyticSearchRequestDto,
  options?: { enabled?: boolean }
): UseQueryResult<KeyMetricsResponseDto> => {
  const newFilter: AnalyticSearchRequestDto = {
    ...filter,
    invoiceStatusSet: processInvoiceStatusSet(filter.invoiceStatusSet),
  };

  return useQuery(
    [...cacheKeysUtil.getKeyMetrics(filter), 'charge-data'],
    async (): Promise<KeyMetricsResponseDto> => (await tFinancialService.api.getKeyMetricsChargeData(useType, newFilter)).data.data,
    {
      enabled: !!filter.currencyReference && options?.enabled !== false,
    }
  );
};

export const useFilters = (
  useType: 'DASHBOARD' | 'REPORT' | 'PERFORMANCE_ASSESSMENT',
  filter: FiltersRequestDto,
  options?: { enabled?: boolean }
): UseQueryResult<FiltersDataDto> => {
  const newFilter: FiltersRequestDto = {
    ...filter,
    invoiceStatusSet: processInvoiceStatusSet(filter.invoiceStatusSet),
  };

  return useQuery(
    cacheKeysUtil.getAnalyticFilters(newFilter),
    async (): Promise<FiltersDataDto> => (await tFinancialService.api.getFiltersMetadata(useType, newFilter)).data.data,
    { enabled: !!newFilter.currencyReference && options?.enabled !== false }
  );
};

// New hook for shipments data to support sequential loading
export const useShipmentsData = (filter: AnalyticSearchRequestDto, options?: { enabled?: boolean }): UseQueryResult<any> => {
  const newFilter: AnalyticSearchRequestDto = {
    ...filter,
    invoiceStatusSet: processInvoiceStatusSet(filter.invoiceStatusSet),
    granularity: filter.granularity || 'MONTHLY', // Default granularity if not provided
  };

  return useQuery(
    ['shipments-data', newFilter],
    async (): Promise<any> => {
      // Using the proper fetchShipmentData API for UI data fetching
      const response = await tFinancialService.api.fetchShipmentData({
        page: { pageNumber: 1, pageSize: 1000 },
        searchRequest: newFilter,
      });
      return response.data;
    },
    {
      enabled: !!newFilter.currencyReference && options?.enabled === true,
    }
  );
};

export const sortAggregatedData = (data: AggregatedDto[], columns: string[], direction: number = -1): AggregatedDto[] => {
  // sort by column 1, then if column 1 is the same, sort by column 2, etc
  const sortedData = [...data];

  sortedData.sort((a, b) => {
    for (const column of columns) {
      if (a[column] < b[column]) {
        return direction * -1;
      }
      if (a[column] > b[column]) {
        return direction;
      }
    }
    return 0;
  });

  return sortedData;
};

export const fillMissingDates = (data: ChartResponseDto, granularity: string): ChartResponseDto => {
  const completeData = { ...data };
  let addUnit;
  let formatString;

  switch (granularity) {
    case 'YEARLY':
      addUnit = addYears;
      formatString = 'yyyy';
      break;
    case 'QUARTERLY':
      addUnit = addQuarters;
      formatString = "yyyy-'Q'Q";
      break;
    case 'MONTHLY':
      addUnit = addMonths;
      formatString = 'yyyy-MMM';
      break;
    case 'WEEKLY':
      addUnit = addWeeks;
      formatString = "RRRR-'W'II";
      break;
    case 'DAILY':
      addUnit = addDays;
      formatString = 'yyyy-MM-dd';
      break;
    default:
      return completeData;
  }

  if (completeData.aggregated.length < 1) {
    return completeData;
  }
  const startDate = parse(completeData.aggregated[0].aggregatedUnit, formatString, new Date());
  const endDate = parse(completeData.aggregated[completeData.aggregated.length - 1].aggregatedUnit, formatString, new Date());

  let currentDate = startDate;
  let index = 0;

  while (currentDate < endDate) {
    const formattedDate = format(currentDate, formatString);
    if (formattedDate !== completeData.aggregated[index].aggregatedUnit) {
      const newAggregated = {} as AggregatedDto;
      newAggregated.aggregatedUnit = formattedDate;
      completeData.aggregated.splice(index, 0, newAggregated);
    }
    index += 1;
    currentDate = addUnit(currentDate, 1);
  }

  return completeData;
};

export const useChartReports = (type?: 'REPORT_ANALYTIC' | 'PERFORMANCE_ASSESSMENT'): UseQueryResult<ChartReportDto[]> =>
  useQuery(cacheKeysUtil.getChartReports(type), async () => (await tFinancialService.api.getChartReports({ type })).data.data);

export const useSaveReport = () => {
  const queryClient = useQueryClient();
  return useMutation(async (chartReportRequest: ChartReportRequestDto) => tFinancialService.api.createChartReport(chartReportRequest), {
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(cacheKeysUtil.getChartReports()),
        queryClient.invalidateQueries(cacheKeysUtil.getChartReports('PERFORMANCE_ASSESSMENT')),
        queryClient.invalidateQueries(cacheKeysUtil.getChartReports('REPORT_ANALYTIC')),
      ]);
    },
  });
};

export const useUpdateReport = () => {
  const queryClient = useQueryClient();
  return useMutation(async (chartReportRequest: ChartReportRequestDto) => tFinancialService.api.updateChartReport(chartReportRequest), {
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(cacheKeysUtil.getChartReports()),
        queryClient.invalidateQueries(cacheKeysUtil.getChartReports('PERFORMANCE_ASSESSMENT')),
        queryClient.invalidateQueries(cacheKeysUtil.getChartReports('REPORT_ANALYTIC')),
      ]);
    },
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  return useMutation(async (chartReportId: string) => tFinancialService.api.deleteChartReport(chartReportId), {
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(cacheKeysUtil.getChartReports()),
        queryClient.invalidateQueries(cacheKeysUtil.getChartReports('PERFORMANCE_ASSESSMENT')),
        queryClient.invalidateQueries(cacheKeysUtil.getChartReports('REPORT_ANALYTIC')),
      ]);
    },
  });
};

type FileType = 'CSV' | 'EXCEL';

type DownloadConfig = {
  api: (query: any, data: any, params?: any) => Promise<any>;
  type: 'async' | 'sync';
};

const downloadFunctions: Record<string, DownloadConfig> = {
  Shipments: { api: tFinancialService.api.exportShipmentData, type: 'async' },
  Charges: { api: tFinancialService.api.exportInvoiceChargesData, type: 'async' },
  Invoices: { api: tFinancialService.api.exportInvoiceData, type: 'async' },
  SAVINGS: { api: tFinancialService.api.getSavingsTableDataDownload, type: 'sync' },
  VOLUMETRIC: { api: tFinancialService.api.getVolumetricTableDataDownload, type: 'sync' },
};

const downloadFile = (response: AxiosResponse<void, any>, fileName: string, type: FileType) => {
  const url = window.URL.createObjectURL(new Blob([response.data as unknown as BlobPart]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}.${type === 'CSV' ? 'csv' : 'xlsx'}`);
  document.body.appendChild(link);
  link.click();
};

export const useDownloadTemplate = (tableTab: TableTab, name: string = 'default_name', formValues, pageSize: number = 10) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const openToast = useToast();

  const download = async (type: FileType) => {
    const safeName = name ? name.replace(/\s/g, '') : 'default_name';
    const fileName = `${safeName}_${formatDate(new Date())}_${tableTab.toLowerCase()}`;

    const selectedDownloadFunction = downloadFunctions[tableTab];

    if (selectedDownloadFunction) {
      setIsLoading(true);
      let apiParams: Record<string, any> = { page: { pageNumber: 1, pageSize } };

      if (tableTab === 'SAVINGS' || tableTab === 'VOLUMETRIC') {
        apiParams = { ...apiParams, performanceAssessmentRequest: sanitizeFormValues(formValues) };
      } else {
        // Report And Analytics Page - sanitize form values to convert empty strings to null
        const sanitizedFiltersRequest = sanitizeFormValues(formValues.filtersRequest);

        // Merge column filters with global filters based on table type
        let finalSearchRequest = sanitizedFiltersRequest;

        // Apply column filters for each table type using the unified builder logic
        // This ensures download params match exactly what is used in the table view (including chargeCount logic)
        const context = tableTab === 'Invoices' ? 'invoice' : tableTab === 'Shipments' ? 'shipment' : tableTab === 'Charges' ? 'charge' : 'invoice';

        if (formValues.unifiedTableFilters) {
          finalSearchRequest = mergeTableFiltersIntoRequest(sanitizedFiltersRequest, formValues.unifiedTableFilters, context);
        }

        apiParams = { ...apiParams, searchRequest: finalSearchRequest };
      }

      try {
        const response = await selectedDownloadFunction.api({ 'file-name': fileName, 'file-type': type }, apiParams, { format: 'blob' });
        if (response.status === 200) {
          if (selectedDownloadFunction.type === 'sync') {
            downloadFile(response, fileName, type);
            setIsFinished(true);
          } else {
            // Async download started successfully - show toast
            openToast({
              title: `${type} ${tableTab} Report request sent.`,
              description: 'Please download the report from the Notifications once available',
              toastType: 'info',
              toastOptions: {
                icon: <img src={DocumentDownloadIcon} alt='download' />,
                closeButton: true, // Show close button as per design
              },
            });
            setIsFinished(true);
          }
        }
      } catch (error) {
        openToastError(new Error('An error occurred while downloading the file. Please try again.'));
      }
      setIsLoading(false);
    }
  };

  return { download, isLoading, isFinished, setIsFinished };
};
